/**
 * Onglet « Avant-projet » — lecture et édition du bloc saisi à la création.
 *
 * Le guide de design réserve le motif numéroté (01 / 02 / 03) aux steppers :
 * cet écran ne reproduit PAS la mise en page de la fiche HTML d'origine, il
 * utilise des grilles clé-valeur denses.
 */
import { useCallback } from 'react';
import { useOutletContext } from 'react-router';
import { Box, Stack, TextField, Typography } from '@mui/material';
import type { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { AP_MILESTONE_KEYS, type AvantProjet } from '@altyor-kronos/shared-types';
import { useAuth } from '../../core/auth/useAuth';
import { updateAvantProjet } from '../../core/data/projectMutations';
import { colors } from '../../core/theme/theme';
import ApRiskTable from './ApRiskTable';
import { AP_MILESTONE_MAP } from './apMilestoneMap';
import { useMilestones } from './useMilestones';
import type { ProjectDetailContext } from './ProjectOverview';

const BASELINE_GRID = {
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr',
  gap: 1.5,
  px: 1.75,
  py: 1,
  borderBottom: `1px solid ${colors.lightGray}`,
} as const;

const fmt = (ts: Timestamp | undefined) =>
  ts ? ts.toDate().toISOString().split('T')[0] : '—';

export default function ProjectAvantProjetTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { project } = useOutletContext<ProjectDetailContext>();
  const { milestones } = useMilestones(project.id);
  const ap: AvantProjet = project.avantProjet ?? {};

  // Miroir de projectScoped.ts projects.update : admin | direction | managers.
  // `quality` et `project_manager` n'existent pas dans le token Entra.
  const canEdit =
    (user?.roles.includes('admin') ?? false) ||
    (user?.roles.includes('direction') ?? false) ||
    (project.team?.managers?.includes(user?.uid ?? '') ?? false);

  const save = useCallback(
    (patch: Partial<AvantProjet>) => void updateAvantProjet(project.id, { ...ap, ...patch }),
    [project.id, ap],
  );

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Stack spacing={1}>
        <Typography variant="h4">{t('projects.avantProjetTab.context')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField size="small" label={t('projects.avantProjetTab.apReference')}
            defaultValue={ap.apReference ?? ''} disabled={!canEdit}
            onBlur={(e) => e.target.value !== (ap.apReference ?? '') && save({ apReference: e.target.value })} />
          <TextField size="small" label={t('projects.avantProjetTab.clientContact')}
            defaultValue={ap.clientContact ?? ''} disabled={!canEdit}
            onBlur={(e) => e.target.value !== (ap.clientContact ?? '') && save({ clientContact: e.target.value })} />
          <TextField size="small" label={t('projects.avantProjetTab.market')}
            defaultValue={ap.market ?? ''} disabled={!canEdit}
            onBlur={(e) => e.target.value !== (ap.market ?? '') && save({ market: e.target.value })} />
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h4">{t('projects.avantProjetTab.targets')}</Typography>

        {/* Le cœur de l'écran : ce qui a été promis au devis face à ce qui est
            planifié aujourd'hui. `designFreeze` n'a pas de jalon en face tant
            que la correspondance n'est pas tranchée — la colonne affiche « — ». */}
        <Box data-testid="ap-baseline-table" sx={{ border: `1px solid ${colors.lightGray}` }}>
          <Box sx={{ ...BASELINE_GRID, backgroundColor: colors.lightGray, fontSize: 11, fontWeight: 700,
                     borderBottom: `2px solid ${colors.bodyText}` }}>
            <Box>{t('projects.avantProjetTab.milestoneCol')}</Box>
            <Box>{t('projects.avantProjetTab.promisedCol')}</Box>
            <Box>{t('projects.avantProjetTab.currentCol')}</Box>
          </Box>
          {AP_MILESTONE_KEYS.map((k) => {
            const promised = ap.baselineDates?.[k];
            const templateId = AP_MILESTONE_MAP[k];
            // Le `templateId` est un CHAMP du jalon, pas son id : les jalons créés
            // par le wizard ont un id auto-généré. Seule la reprise du PAD écrit
            // sous `milestones/{templateId}`, d'où le repli sur l'id — même
            // résolution que `tools/pad/import.ts`.
            const current = templateId
              ? milestones.find((m) => (m.data.templateId ?? m.id) === templateId)?.data
              : undefined;
            const effective = current?.rescheduledDate ?? current?.targetDate;
            return (
              <Box key={k} sx={BASELINE_GRID} data-testid={`ap-baseline-${k}`}>
                <Typography variant="body2">{t(`projects.avantProjetTab.milestone.${k}`)}</Typography>
                <Typography variant="body2">{fmt(promised)}</Typography>
                <Typography variant="body2">{templateId ? fmt(effective) : '—'}</Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField size="small" label={t('projects.avantProjetTab.targetProductCost')}
            defaultValue={ap.targetProductCost ?? ''} disabled={!canEdit} type="number"
            onBlur={(e) => save({ targetProductCost: e.target.value ? Number(e.target.value) : undefined })} />
          <TextField size="small" label={t('projects.avantProjetTab.targetSellPrice')}
            defaultValue={ap.targetSellPrice ?? ''} disabled={!canEdit} type="number"
            onBlur={(e) => save({ targetSellPrice: e.target.value ? Number(e.target.value) : undefined })} />
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h4">{t('projects.avantProjetTab.risks')}</Typography>
        <ApRiskTable risks={ap.risks ?? []} canEdit={canEdit} onChange={(risks) => save({ risks })} />
      </Stack>
    </Stack>
  );
}
